import type {
  ClassicalPlanet,
  CourtRank,
  Decan,
  Dignity,
  Element,
  HebrewLetter,
  QabalisticWorld,
  QliphahName,
  SephirahName,
  Suit,
  ZodiacModality,
  ZodiacSign,
} from '@lilith-arcana-os/domain';

export interface SuitProfile {
  suit: Suit;
  element: Element;
  world: QabalisticWorld;
  color: string;
  gemstone: string;
  incense: string;
  symbol: string;
  symbol_emoji: string;
  body_focus: string;
  love_focus: string;
  work_focus: string;
  spirit_focus: string;
  psyche_focus: string;
  shadow_focus: string;
  landscape: string;
  pip_titles: Record<number, string>;
}

export interface NumberProfile {
  number: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
  label: string;
  archetype: string;
  keywords: [string, string, string];
  numerology: string;
  uplift: string;
  shadow: string;
}

export interface CourtProfile {
  rank: CourtRank;
  element_secondary: Element;
  astro_primary: string;
  keywords: [string, string, string];
  prompt: string;
}

export interface DecanSeed {
  zodiac_sign: ZodiacSign;
  decan_position: 1 | 2 | 3;
  degrees: string;
  chaldean_ruler: ClassicalPlanet;
  dates: {
    start: string;
    end: string;
  };
  body_region: string;
}

export interface MajorCardSeed {
  id: number;
  name: string;
  slug: string;
  number: number;
  hermetic_title: string;
  element: Element;
  astro_primary: string;
  path_number: number;
  hebrew_letter: HebrewLetter;
  hebrew_char: string;
  tree_position: string;
  zodiac_sign: ZodiacSign | null;
  zodiac_modality: ZodiacModality | null;
  solar_dates: {
    start: string;
    end: string;
  } | null;
  qliphah: QliphahName | null;
  lilith_qliphah_link: QliphahName | null;
  gemstone: string;
  incense: string;
  color_primary: string;
  body_part: string | null;
  musical_note: string | null;
  keywords_up: string[];
  keywords_rev: string[];
  meaning_up: string;
  meaning_rev: string;
  meaning_love: string;
  meaning_work: string;
  meaning_spirit: string;
  meaning_psyche: string;
  lilith_voice: string;
  qliphotic_meaning: string;
  simbologia: string;
  symbol_emoji: string;
  numerology_meaning: string;
  dignity?: Dignity | null;
}

export const SUITS = ['Bastoni', 'Coppe', 'Spade', 'Denari'] as const satisfies readonly Suit[];
export const MODES_SUPPORTED = ['standard', 'lilith', 'qliphotic', 'kabbalistic'] as const;
export const SEPHIROTH_BY_NUMBER: Record<number, SephirahName> = {
  1: 'Kether',
  2: 'Chokmah',
  3: 'Binah',
  4: 'Chesed',
  5: 'Geburah',
  6: 'Tiphareth',
  7: 'Netzach',
  8: 'Hod',
  9: 'Yesod',
  10: 'Malkuth',
};

export const QLIPHAH_BY_SEPHIRAH: Record<SephirahName, QliphahName> = {
  Kether: 'Thaumiel',
  Chokmah: 'Ghagiel',
  Binah: 'Satariel',
  Chesed: 'Gha_agsheblah',
  Geburah: 'Golachab',
  Tiphareth: 'Thagirion',
  Netzach: 'A_arab_Zaraq',
  Hod: 'Samael',
  Yesod: 'Gamaliel',
  Malkuth: 'Nahemoth',
};

export const ZODIAC_MODALITY_BY_SIGN: Record<ZodiacSign, ZodiacModality> = {
  Ariete: 'Cardinale',
  Toro: 'Fisso',
  Gemelli: 'Mutabile',
  Cancro: 'Cardinale',
  Leone: 'Fisso',
  Vergine: 'Mutabile',
  Bilancia: 'Cardinale',
  Scorpione: 'Fisso',
  Sagittario: 'Mutabile',
  Capricorno: 'Cardinale',
  Acquario: 'Fisso',
  Pesci: 'Mutabile',
};

export const SUIT_PROFILES: Record<Suit, SuitProfile> = {
  Bastoni: {
    suit: 'Bastoni',
    element: 'Fuoco',
    world: 'Atziluth',
    color: '#c9a96e',
    gemstone: 'corniola',
    incense: 'sangue di drago',
    symbol: 'bastone',
    symbol_emoji: '🔥',
    body_focus: 'spalle e braccia',
    love_focus: 'desiderio espresso con franchezza e iniziativa',
    work_focus: 'leadership, lancio di progetti e velocita decisionale',
    spirit_focus: 'vocazione che chiede di essere accesa, non solo pensata',
    psyche_focus: 'energia, rabbia creativa, bisogno di agire e di segnare il territorio',
    shadow_focus: 'bruciatura, dominazione e impazienza sacralizzata',
    landscape: 'una radura incendiata dall alba, con legno vivo e brace sotto i piedi',
    pip_titles: {
      1: 'Radice dei poteri del Fuoco',
      2: 'Dominio',
      3: 'Virtu',
      4: 'Completamento',
      5: 'Conflitto',
      6: 'Vittoria',
      7: 'Valore',
      8: 'Rapidita',
      9: 'Forza',
      10: 'Oppressione',
    },
  },
  Coppe: {
    suit: 'Coppe',
    element: 'Acqua',
    world: 'Briah',
    color: '#c96e8b',
    gemstone: 'pietra di luna',
    incense: 'mirra dolce',
    symbol: 'coppa',
    symbol_emoji: '🌊',
    body_focus: 'petto e respiro',
    love_focus: 'intimita, risonanza affettiva e nutrimento reciproco',
    work_focus: 'clima relazionale, alleanze e sensibilita nel negoziare',
    spirit_focus: 'ascolto dell immaginazione, devozione e soglia visionaria',
    psyche_focus: 'legame, nostalgia, bisogno di contenimento e capacita di fidarsi',
    shadow_focus: 'fusione, dipendenza, sedazione emotiva e dolcezza che copre il vuoto',
    landscape: 'una vasca rituale sotto la luna, con acqua calma e riflessi cremisi',
    pip_titles: {
      1: 'Radice dei poteri dell Acqua',
      2: 'Amore',
      3: 'Abbondanza',
      4: 'Lusso',
      5: 'Disillusione',
      6: 'Piacere',
      7: 'Fantasia',
      8: 'Indolenza',
      9: 'Felicita materiale',
      10: 'Sazieta',
    },
  },
  Spade: {
    suit: 'Spade',
    element: 'Aria',
    world: 'Yetzirah',
    color: '#9db3c7',
    gemstone: 'ametista pallida',
    incense: 'olibano chiaro',
    symbol: 'spada',
    symbol_emoji: '🜁',
    body_focus: 'testa e sistema nervoso',
    love_focus: 'parole, verita difficili, confini e lucidita nel legame',
    work_focus: 'strategia, analisi, conflitto aperto e precisione tattica',
    spirit_focus: 'discernimento, taglio dell illusione e disciplina mentale',
    psyche_focus: 'ansia, chiarezza, spirali mentali e coraggio di nominare il nodo',
    shadow_focus: 'ipercontrollo, ferita verbale, gelo e scissione',
    landscape: 'una pianura ventosa con lame infisse e un cielo tagliato da nubi sottili',
    pip_titles: {
      1: 'Radice dei poteri dell Aria',
      2: 'Pace',
      3: 'Dolore',
      4: 'Tregua',
      5: 'Sconfitta',
      6: 'Scienza',
      7: 'Futilita',
      8: 'Interferenza',
      9: 'Crudelta',
      10: 'Rovina',
    },
  },
  Denari: {
    suit: 'Denari',
    element: 'Terra',
    world: 'Assiah',
    color: '#8aa06d',
    gemstone: 'onice verde',
    incense: 'patchouli',
    symbol: 'denaro',
    symbol_emoji: '🌿',
    body_focus: 'gambe, pelle e mani',
    love_focus: 'cura concreta, tempo condiviso, affidabilita e costruzione lenta',
    work_focus: 'mestiere, risorse, stabilita e risultati misurabili',
    spirit_focus: 'sacralita del corpo, del pane e del ritmo stagionale',
    psyche_focus: 'sicurezza, valore personale, attaccamento e paura della perdita',
    shadow_focus: 'avidita, inerzia, impoverimento dell anima nel solo utile',
    landscape: 'un giardino al crepuscolo, con monete appese ai rami e terra umida',
    pip_titles: {
      1: 'Radice dei poteri della Terra',
      2: 'Cambiamento armonico',
      3: 'Lavoro',
      4: 'Potere terreno',
      5: 'Preoccupazione',
      6: 'Successo materiale',
      7: 'Fallimento apparente',
      8: 'Prudenza',
      9: 'Guadagno',
      10: 'Ricchezza',
    },
  },
};

export const NUMBER_PROFILES: Record<1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10, NumberProfile> = {
  1: {
    number: 1,
    label: 'Asso',
    archetype: 'seme primario',
    keywords: ['inizio', 'seme', 'chiamata'],
    numerology: 'L uno concentra la spinta iniziale e la rende direzione. Non mostra ancora il risultato: mostra la necessita di un primo atto.',
    uplift: 'apre una corrente pura e non ancora contaminata',
    shadow: 'diventa impulso cieco o promessa non incarnata',
  },
  2: {
    number: 2,
    label: 'Due',
    archetype: 'polarita viva',
    keywords: ['scelta', 'duello', 'bilanciamento'],
    numerology: 'Il due introduce il rapporto. Chiede una misura tra forze che si riconoscono e si mettono alla prova.',
    uplift: 'cerca una forma di accordo dinamico',
    shadow: 'si irrigidisce in stallo o schermaglia sterile',
  },
  3: {
    number: 3,
    label: 'Tre',
    archetype: 'gestazione',
    keywords: ['espansione', 'triangolazione', 'sviluppo'],
    numerology: 'Il tre genera scena, spazio e respiro. E la prima forma di crescita che esce dal puro confronto.',
    uplift: 'mette in circolo la possibilita',
    shadow: 'si disperde in eccesso o vanita',
  },
  4: {
    number: 4,
    label: 'Quattro',
    archetype: 'struttura',
    keywords: ['fondazione', 'stabilita', 'contenimento'],
    numerology: 'Il quattro costruisce telaio e confine. Dona durata, ma chiede di non scambiare l appoggio per gabbia.',
    uplift: 'stabilizza senza soffocare',
    shadow: 'diventa rigidita o immobilismo difensivo',
  },
  5: {
    number: 5,
    label: 'Cinque',
    archetype: 'frattura',
    keywords: ['crisi', 'attrito', 'squilibrio'],
    numerology: 'Il cinque rompe la forma e rivela la tensione nascosta. E scomodo, ma costringe a rivedere cio che era dato per saldo.',
    uplift: 'smaschera il nodo reale',
    shadow: 'precipita in conflitto sterile o privazione',
  },
  6: {
    number: 6,
    label: 'Sei',
    archetype: 'ricomposizione',
    keywords: ['armonizzazione', 'scambio', 'passaggio'],
    numerology: 'Il sei cerca una bellezza funzionale: non nega la crisi, ma prova a ordinarla in un nuovo patto.',
    uplift: 'ricuce con intelligenza e misura',
    shadow: 'abbellisce troppo o evita il punto vivo',
  },
  7: {
    number: 7,
    label: 'Sette',
    archetype: 'prova di soglia',
    keywords: ['sfida', 'strategia', 'tenuta'],
    numerology: 'Il sette mette il cammino sotto esame. Chiede astuzia, disciplina e la capacita di stare in una prova senza perdere asse.',
    uplift: 'affina la postura interiore',
    shadow: 'porta in sospetto, furbizia o dispersione',
  },
  8: {
    number: 8,
    label: 'Otto',
    archetype: 'compressione operativa',
    keywords: ['ritmo', 'disciplina', 'canalizzazione'],
    numerology: 'L otto condensa energia e la mette al lavoro. Può essere maestria oppure gabbia, a seconda di quanto resta vivo il centro.',
    uplift: 'trasforma il flusso in gesto efficace',
    shadow: 'si chiude in automatismo o controllo',
  },
  9: {
    number: 9,
    label: 'Nove',
    archetype: 'maturazione',
    keywords: ['culmine', 'profondita', 'tesa attesa'],
    numerology: 'Il nove porta la corrente verso il bordo della sua forma. Non e ancora fine, ma intensita piena che chiede coscienza.',
    uplift: 'mostra la qualita raggiunta',
    shadow: 'spinge verso saturazione o ossessione',
  },
  10: {
    number: 10,
    label: 'Dieci',
    archetype: 'saturazione del ciclo',
    keywords: ['esito', 'peso', 'trasmissione'],
    numerology: 'Il dieci chiude, moltiplica e redistribuisce. Fa vedere che ogni compimento porta con sé un carico e una eredita.',
    uplift: 'consegna il raccolto del ciclo',
    shadow: 'schiaccia sotto il troppo o svuota dopo l eccesso',
  },
};

export const COURT_PROFILES: Record<CourtRank, CourtProfile> = {
  Re: {
    rank: 'Re',
    element_secondary: 'Fuoco',
    astro_primary: 'fuoco sovrano',
    keywords: ['autorita', 'direzione', 'ardore'],
    prompt: 'incarna la legge del seme nel suo stato piu consapevole',
  },
  Regina: {
    rank: 'Regina',
    element_secondary: 'Acqua',
    astro_primary: 'acqua regale',
    keywords: ['magnetismo', 'ricezione', 'custodia'],
    prompt: 'traduce il seme in campo emotivo, simbolico e relazionale',
  },
  Cavaliere: {
    rank: 'Cavaliere',
    element_secondary: 'Aria',
    astro_primary: 'aria mobile',
    keywords: ['movimento', 'visione', 'ricerca'],
    prompt: 'porta la corrente fuori dal castello e la mette in cammino',
  },
  Fante: {
    rank: 'Fante',
    element_secondary: 'Terra',
    astro_primary: 'terra d ascolto',
    keywords: ['apprendimento', 'soglia', 'incarnazione'],
    prompt: 'riceve la corrente e la rende corpo, gesto e studio',
  },
};

export const MINOR_CARD_NAMES: Record<1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10, string> = {
  1: 'Asso',
  2: 'Due',
  3: 'Tre',
  4: 'Quattro',
  5: 'Cinque',
  6: 'Sei',
  7: 'Sette',
  8: 'Otto',
  9: 'Nove',
  10: 'Dieci',
};

export const MAJOR_ASTRO_COLORS: Record<string, string> = {
  Aria: '#c9d6e2',
  Mercurio: '#c0b07a',
  Luna: '#c7bed8',
  Venere: '#c96e8b',
  Ariete: '#c9734f',
  Toro: '#8aa06d',
  Gemelli: '#d4c18c',
  Cancro: '#95a8c6',
  Leone: '#d79b45',
  Vergine: '#aab783',
  Giove: '#6d8fc3',
  Bilancia: '#d6a3b7',
  Acqua: '#7d9bb8',
  Scorpione: '#7a4f5f',
  Sagittario: '#c98c5a',
  Capricorno: '#6f7567',
  Marte: '#b35a58',
  Acquario: '#7bb6c1',
  Pesci: '#8f84c5',
  Sole: '#d5aa4d',
  Fuoco: '#d06e4a',
  Saturno: '#6f6a7c',
};

const SIGN_SEQUENCE: ZodiacSign[] = [
  'Ariete',
  'Toro',
  'Gemelli',
  'Cancro',
  'Leone',
  'Vergine',
  'Bilancia',
  'Scorpione',
  'Sagittario',
  'Capricorno',
  'Acquario',
  'Pesci',
];

const DECAN_DATE_MAP: Record<ZodiacSign, [DecanSeed['dates'], DecanSeed['dates'], DecanSeed['dates']]> = {
  Ariete: [
    { start: '21-03', end: '30-03' },
    { start: '31-03', end: '09-04' },
    { start: '10-04', end: '19-04' },
  ],
  Toro: [
    { start: '20-04', end: '29-04' },
    { start: '30-04', end: '09-05' },
    { start: '10-05', end: '20-05' },
  ],
  Gemelli: [
    { start: '21-05', end: '31-05' },
    { start: '01-06', end: '10-06' },
    { start: '11-06', end: '20-06' },
  ],
  Cancro: [
    { start: '21-06', end: '01-07' },
    { start: '02-07', end: '11-07' },
    { start: '12-07', end: '22-07' },
  ],
  Leone: [
    { start: '23-07', end: '01-08' },
    { start: '02-08', end: '12-08' },
    { start: '13-08', end: '22-08' },
  ],
  Vergine: [
    { start: '23-08', end: '01-09' },
    { start: '02-09', end: '12-09' },
    { start: '13-09', end: '22-09' },
  ],
  Bilancia: [
    { start: '23-09', end: '02-10' },
    { start: '03-10', end: '12-10' },
    { start: '13-10', end: '22-10' },
  ],
  Scorpione: [
    { start: '23-10', end: '01-11' },
    { start: '02-11', end: '11-11' },
    { start: '12-11', end: '21-11' },
  ],
  Sagittario: [
    { start: '22-11', end: '01-12' },
    { start: '02-12', end: '11-12' },
    { start: '12-12', end: '21-12' },
  ],
  Capricorno: [
    { start: '22-12', end: '31-12' },
    { start: '01-01', end: '10-01' },
    { start: '11-01', end: '19-01' },
  ],
  Acquario: [
    { start: '20-01', end: '29-01' },
    { start: '30-01', end: '08-02' },
    { start: '09-02', end: '18-02' },
  ],
  Pesci: [
    { start: '19-02', end: '28-02' },
    { start: '01-03', end: '10-03' },
    { start: '11-03', end: '20-03' },
  ],
};

const BODY_REGION_BY_SIGN: Record<ZodiacSign, string> = {
  Ariete: 'testa',
  Toro: 'gola',
  Gemelli: 'braccia e polmoni',
  Cancro: 'torace e stomaco',
  Leone: 'cuore e schiena',
  Vergine: 'addome',
  Bilancia: 'reni e bacino',
  Scorpione: 'pelvi e sessualita',
  Sagittario: 'cosce',
  Capricorno: 'ginocchia',
  Acquario: 'caviglie',
  Pesci: 'piedi',
};

const CHALDEAN_RULERS: ClassicalPlanet[] = [
  'Marte',
  'Sole',
  'Venere',
  'Mercurio',
  'Luna',
  'Saturno',
  'Giove',
];

const decanRulerFor = (sign: ZodiacSign, position: 1 | 2 | 3): ClassicalPlanet => {
  const signIndex = SIGN_SEQUENCE.indexOf(sign);
  const offset = signIndex * 3 + (position - 1);
  return CHALDEAN_RULERS[offset % CHALDEAN_RULERS.length] ?? 'Marte';
};

export const MINOR_DECANS: Record<Suit, DecanSeed[]> = {
  Bastoni: [
    { zodiac_sign: 'Ariete', decan_position: 1, degrees: '0-10', chaldean_ruler: decanRulerFor('Ariete', 1), dates: DECAN_DATE_MAP.Ariete[0], body_region: BODY_REGION_BY_SIGN.Ariete },
    { zodiac_sign: 'Ariete', decan_position: 2, degrees: '10-20', chaldean_ruler: decanRulerFor('Ariete', 2), dates: DECAN_DATE_MAP.Ariete[1], body_region: BODY_REGION_BY_SIGN.Ariete },
    { zodiac_sign: 'Ariete', decan_position: 3, degrees: '20-30', chaldean_ruler: decanRulerFor('Ariete', 3), dates: DECAN_DATE_MAP.Ariete[2], body_region: BODY_REGION_BY_SIGN.Ariete },
    { zodiac_sign: 'Leone', decan_position: 1, degrees: '0-10', chaldean_ruler: decanRulerFor('Leone', 1), dates: DECAN_DATE_MAP.Leone[0], body_region: BODY_REGION_BY_SIGN.Leone },
    { zodiac_sign: 'Leone', decan_position: 2, degrees: '10-20', chaldean_ruler: decanRulerFor('Leone', 2), dates: DECAN_DATE_MAP.Leone[1], body_region: BODY_REGION_BY_SIGN.Leone },
    { zodiac_sign: 'Leone', decan_position: 3, degrees: '20-30', chaldean_ruler: decanRulerFor('Leone', 3), dates: DECAN_DATE_MAP.Leone[2], body_region: BODY_REGION_BY_SIGN.Leone },
    { zodiac_sign: 'Sagittario', decan_position: 1, degrees: '0-10', chaldean_ruler: decanRulerFor('Sagittario', 1), dates: DECAN_DATE_MAP.Sagittario[0], body_region: BODY_REGION_BY_SIGN.Sagittario },
    { zodiac_sign: 'Sagittario', decan_position: 2, degrees: '10-20', chaldean_ruler: decanRulerFor('Sagittario', 2), dates: DECAN_DATE_MAP.Sagittario[1], body_region: BODY_REGION_BY_SIGN.Sagittario },
    { zodiac_sign: 'Sagittario', decan_position: 3, degrees: '20-30', chaldean_ruler: decanRulerFor('Sagittario', 3), dates: DECAN_DATE_MAP.Sagittario[2], body_region: BODY_REGION_BY_SIGN.Sagittario },
  ],
  Coppe: [
    { zodiac_sign: 'Cancro', decan_position: 1, degrees: '0-10', chaldean_ruler: decanRulerFor('Cancro', 1), dates: DECAN_DATE_MAP.Cancro[0], body_region: BODY_REGION_BY_SIGN.Cancro },
    { zodiac_sign: 'Cancro', decan_position: 2, degrees: '10-20', chaldean_ruler: decanRulerFor('Cancro', 2), dates: DECAN_DATE_MAP.Cancro[1], body_region: BODY_REGION_BY_SIGN.Cancro },
    { zodiac_sign: 'Cancro', decan_position: 3, degrees: '20-30', chaldean_ruler: decanRulerFor('Cancro', 3), dates: DECAN_DATE_MAP.Cancro[2], body_region: BODY_REGION_BY_SIGN.Cancro },
    { zodiac_sign: 'Scorpione', decan_position: 1, degrees: '0-10', chaldean_ruler: decanRulerFor('Scorpione', 1), dates: DECAN_DATE_MAP.Scorpione[0], body_region: BODY_REGION_BY_SIGN.Scorpione },
    { zodiac_sign: 'Scorpione', decan_position: 2, degrees: '10-20', chaldean_ruler: decanRulerFor('Scorpione', 2), dates: DECAN_DATE_MAP.Scorpione[1], body_region: BODY_REGION_BY_SIGN.Scorpione },
    { zodiac_sign: 'Scorpione', decan_position: 3, degrees: '20-30', chaldean_ruler: decanRulerFor('Scorpione', 3), dates: DECAN_DATE_MAP.Scorpione[2], body_region: BODY_REGION_BY_SIGN.Scorpione },
    { zodiac_sign: 'Pesci', decan_position: 1, degrees: '0-10', chaldean_ruler: decanRulerFor('Pesci', 1), dates: DECAN_DATE_MAP.Pesci[0], body_region: BODY_REGION_BY_SIGN.Pesci },
    { zodiac_sign: 'Pesci', decan_position: 2, degrees: '10-20', chaldean_ruler: decanRulerFor('Pesci', 2), dates: DECAN_DATE_MAP.Pesci[1], body_region: BODY_REGION_BY_SIGN.Pesci },
    { zodiac_sign: 'Pesci', decan_position: 3, degrees: '20-30', chaldean_ruler: decanRulerFor('Pesci', 3), dates: DECAN_DATE_MAP.Pesci[2], body_region: BODY_REGION_BY_SIGN.Pesci },
  ],
  Spade: [
    { zodiac_sign: 'Bilancia', decan_position: 1, degrees: '0-10', chaldean_ruler: decanRulerFor('Bilancia', 1), dates: DECAN_DATE_MAP.Bilancia[0], body_region: BODY_REGION_BY_SIGN.Bilancia },
    { zodiac_sign: 'Bilancia', decan_position: 2, degrees: '10-20', chaldean_ruler: decanRulerFor('Bilancia', 2), dates: DECAN_DATE_MAP.Bilancia[1], body_region: BODY_REGION_BY_SIGN.Bilancia },
    { zodiac_sign: 'Bilancia', decan_position: 3, degrees: '20-30', chaldean_ruler: decanRulerFor('Bilancia', 3), dates: DECAN_DATE_MAP.Bilancia[2], body_region: BODY_REGION_BY_SIGN.Bilancia },
    { zodiac_sign: 'Acquario', decan_position: 1, degrees: '0-10', chaldean_ruler: decanRulerFor('Acquario', 1), dates: DECAN_DATE_MAP.Acquario[0], body_region: BODY_REGION_BY_SIGN.Acquario },
    { zodiac_sign: 'Acquario', decan_position: 2, degrees: '10-20', chaldean_ruler: decanRulerFor('Acquario', 2), dates: DECAN_DATE_MAP.Acquario[1], body_region: BODY_REGION_BY_SIGN.Acquario },
    { zodiac_sign: 'Acquario', decan_position: 3, degrees: '20-30', chaldean_ruler: decanRulerFor('Acquario', 3), dates: DECAN_DATE_MAP.Acquario[2], body_region: BODY_REGION_BY_SIGN.Acquario },
    { zodiac_sign: 'Gemelli', decan_position: 1, degrees: '0-10', chaldean_ruler: decanRulerFor('Gemelli', 1), dates: DECAN_DATE_MAP.Gemelli[0], body_region: BODY_REGION_BY_SIGN.Gemelli },
    { zodiac_sign: 'Gemelli', decan_position: 2, degrees: '10-20', chaldean_ruler: decanRulerFor('Gemelli', 2), dates: DECAN_DATE_MAP.Gemelli[1], body_region: BODY_REGION_BY_SIGN.Gemelli },
    { zodiac_sign: 'Gemelli', decan_position: 3, degrees: '20-30', chaldean_ruler: decanRulerFor('Gemelli', 3), dates: DECAN_DATE_MAP.Gemelli[2], body_region: BODY_REGION_BY_SIGN.Gemelli },
  ],
  Denari: [
    { zodiac_sign: 'Capricorno', decan_position: 1, degrees: '0-10', chaldean_ruler: decanRulerFor('Capricorno', 1), dates: DECAN_DATE_MAP.Capricorno[0], body_region: BODY_REGION_BY_SIGN.Capricorno },
    { zodiac_sign: 'Capricorno', decan_position: 2, degrees: '10-20', chaldean_ruler: decanRulerFor('Capricorno', 2), dates: DECAN_DATE_MAP.Capricorno[1], body_region: BODY_REGION_BY_SIGN.Capricorno },
    { zodiac_sign: 'Capricorno', decan_position: 3, degrees: '20-30', chaldean_ruler: decanRulerFor('Capricorno', 3), dates: DECAN_DATE_MAP.Capricorno[2], body_region: BODY_REGION_BY_SIGN.Capricorno },
    { zodiac_sign: 'Toro', decan_position: 1, degrees: '0-10', chaldean_ruler: decanRulerFor('Toro', 1), dates: DECAN_DATE_MAP.Toro[0], body_region: BODY_REGION_BY_SIGN.Toro },
    { zodiac_sign: 'Toro', decan_position: 2, degrees: '10-20', chaldean_ruler: decanRulerFor('Toro', 2), dates: DECAN_DATE_MAP.Toro[1], body_region: BODY_REGION_BY_SIGN.Toro },
    { zodiac_sign: 'Toro', decan_position: 3, degrees: '20-30', chaldean_ruler: decanRulerFor('Toro', 3), dates: DECAN_DATE_MAP.Toro[2], body_region: BODY_REGION_BY_SIGN.Toro },
    { zodiac_sign: 'Vergine', decan_position: 1, degrees: '0-10', chaldean_ruler: decanRulerFor('Vergine', 1), dates: DECAN_DATE_MAP.Vergine[0], body_region: BODY_REGION_BY_SIGN.Vergine },
    { zodiac_sign: 'Vergine', decan_position: 2, degrees: '10-20', chaldean_ruler: decanRulerFor('Vergine', 2), dates: DECAN_DATE_MAP.Vergine[1], body_region: BODY_REGION_BY_SIGN.Vergine },
    { zodiac_sign: 'Vergine', decan_position: 3, degrees: '20-30', chaldean_ruler: decanRulerFor('Vergine', 3), dates: DECAN_DATE_MAP.Vergine[2], body_region: BODY_REGION_BY_SIGN.Vergine },
  ],
};

export const uniq = (values: string[]): string[] => Array.from(new Set(values.filter((value) => value.trim().length > 0)));

export const normalizeToken = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const titleCase = (value: string): string => value.replace(/(^|\s)([a-z])/g, (_match, prefix, letter) => `${prefix}${letter.toUpperCase()}`);

export const fitMajorColor = (astroPrimary: string): string =>
  MAJOR_ASTRO_COLORS[astroPrimary] ?? '#c9a96e';

export const mirrorCardId = (id: number): number | null => {
  if (id >= 23 && id <= 36) {
    return id + 14;
  }

  if (id >= 37 && id <= 50) {
    return id - 14;
  }

  if (id >= 51 && id <= 64) {
    return id + 14;
  }

  if (id >= 65 && id <= 78) {
    return id - 14;
  }

  return null;
};

export const signShortLabel = (sign: ZodiacSign, position: 1 | 2 | 3): string =>
  `${sign} ${position}`;

export const buildDecan = (
  seed: DecanSeed,
  tarotCardId: number,
  hermeticTitle: string,
  landscape: string,
): Decan => ({
  number: SIGN_SEQUENCE.indexOf(seed.zodiac_sign) * 3 + seed.decan_position,
  zodiac_sign: seed.zodiac_sign,
  decan_position: seed.decan_position,
  degrees: seed.degrees,
  chaldean_ruler: seed.chaldean_ruler,
  dates: seed.dates,
  tarot_card_id: tarotCardId,
  hermetic_title: hermeticTitle,
  angels_shemhamphorash: {
    day: {
      name: `Custode diurno di ${signShortLabel(seed.zodiac_sign, seed.decan_position)}`,
      hebrew: `${normalizeToken(seed.zodiac_sign).toUpperCase()}-${seed.decan_position}-D`,
      psalm_verse: `Invocazione diurna per ${seed.zodiac_sign.toLowerCase()} ${seed.decan_position}`,
    },
    night: {
      name: `Custode notturno di ${signShortLabel(seed.zodiac_sign, seed.decan_position)}`,
      hebrew: `${normalizeToken(seed.zodiac_sign).toUpperCase()}-${seed.decan_position}-N`,
      psalm_verse: `Invocazione notturna per ${seed.zodiac_sign.toLowerCase()} ${seed.decan_position}`,
    },
  },
  goetia_demon: null,
  picatrix_image: `${titleCase(landscape)} riflesso nel decano di ${seed.zodiac_sign.toLowerCase()}.`,
  body_region: seed.body_region,
  geomantic_figure: null,
});
